import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { User, Phone, MapPin, Save, Heart, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', address: '', date_of_birth: '',
    next_of_kin: '', medical_notes: '', preferences: ''
  });

  useEffect(() => {
    axios.get(`${API_URL}/api/client-portal/profile`, { withCredentials: true })
      .then(res => {
        setProfile(res.data);
        setForm({
          name: res.data.name || '',
          phone: res.data.phone || '',
          address: res.data.address || '',
          date_of_birth: res.data.date_of_birth || '',
          next_of_kin: res.data.next_of_kin || '',
          medical_notes: res.data.medical_notes || '',
          preferences: res.data.preferences || '',
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/client-portal/profile`, form, { withCredentials: true });
      toast.success('Profile updated');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-profile-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">My Profile</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your personal information</p>
        </div>

        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <User className="w-5 h-5 text-[#00A6A0]" /> Personal Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Full Name</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  className="bg-muted border-border" data-testid="profile-name" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Phone</label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  className="bg-muted border-border" data-testid="profile-phone" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Date of Birth</label>
                <Input type="date" value={form.date_of_birth}
                  onChange={e => setForm({...form, date_of_birth: e.target.value})}
                  className="bg-muted border-border" data-testid="profile-dob" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Next of Kin</label>
                <Input value={form.next_of_kin} onChange={e => setForm({...form, next_of_kin: e.target.value})}
                  className="bg-muted border-border" data-testid="profile-nok" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Address</label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                className="bg-muted border-border" data-testid="profile-address" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Medical Notes</label>
              <Textarea value={form.medical_notes} rows={3}
                onChange={e => setForm({...form, medical_notes: e.target.value})}
                className="bg-muted border-border" data-testid="profile-medical" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Care Preferences</label>
              <Textarea value={form.preferences} rows={2}
                onChange={e => setForm({...form, preferences: e.target.value})}
                className="bg-muted border-border" data-testid="profile-prefs" />
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSave} disabled={saving} className="btn-primary gap-2" data-testid="save-profile-btn">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
